import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import { useAuth } from "@/auth";
import { ChevronIcon, LogoutIcon, EmailIcon } from "@/assets/icons";
import lrmuitineLogo from "@/assets/images/lrmuitine_logo.png";
import { getPersonDisplayName } from "@/utils";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/components";
import { useQuery } from "@tanstack/react-query";
import { getUnreadNotificationsCount } from "@/generated/ui-api";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useAccessibility } from "@/shared/contexts/AccessibilityContext";
import AccessibilityIcon from "@/assets/icons/AccessibilityIcon";

export const LOGOUT_TYPES = [
  { key: "logoutLocal", localLogout: true },
  { key: "logoutAll", localLogout: false },
] as const;

interface LogoutTypePickerProps {
  isMobile?: boolean;
  variant?: "header" | "navbar";
}

export function LogoutTypePicker({
  isMobile = false,
  variant = "header",
}: LogoutTypePickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const auth = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  if (isMobile) {
    return (
      <div className="flex items-center gap-2">
        {LOGOUT_TYPES.map(logoutType => (
          <button
            key={logoutType.key}
            onClick={async () => {
              auth?.logout(logoutType.localLogout);
            }}
          >
            {t(`auth.${logoutType.key}`)}
          </button>
        ))}
      </div>
    );
  }

  const isHeader = variant === "header";

  const buttonClasses = isHeader
    ? ""
    : "text-sm text-white hover:text-gray-300 cursor-pointer py-[0.5rem] px-[1rem] h-[36px] line-height-[1.25rem] text-sm flex items-center gap-1";

  return (
    <div className="relative" ref={dropdownRef}>
      {isHeader ? (
        <Button
          variant="ghost-error"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t("auth.logout") || "Logout"}
          aria-expanded={isOpen}
          className="bg-white flex items-center gap-1 p-0!"
        >
          {t("auth.logout")}
          <LogoutIcon className="ml-1" />
        </Button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t("auth.logout") || "Logout"}
          aria-expanded={isOpen}
          aria-haspopup="true"
          className={buttonClasses}
        >
          {t("auth.logout")}
          <ChevronIcon
            size={16}
            className={isOpen ? "rotate-180" : ""}
            direction="down"
          />
        </button>
      )}
      {isOpen && (
        <div className="absolute right-0 w-max mt-1 bg-white rounded shadow-lg z-50 border border-gray-200">
          {LOGOUT_TYPES.map(logoutType => (
            <button
              key={logoutType.key}
              onClick={async () => {
                auth.logout(logoutType.localLogout);
              }}
              className="flex w-full text-left py-2 px-4 h-9 line-height-[1.25rem] text-sm text-gray-800 hover:bg-gray-100 first:rounded-t last:rounded-b border-0 bg-transparent cursor-pointer"
            >
              {t(`auth.${logoutType.key}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Logo() {
  const { t } = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    if (auth?.user) {
      navigate("/reports/summaries");
    } else {
      navigate("/");
    }
  };

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex">
        <div
          onClick={handleLogoClick}
          className="flex cursor-pointer hover:opacity-80 transition-opacity"
        >
          <img className="h-16 w-16" src={lrmuitineLogo} alt="LR muitinė" />
          <div className="ml-4 flex items-center space-x-4">
            <h1 className="text-3xl font-bold text-layout-palette idais-title">
              {t("header.title")}
            </h1>
          </div>
        </div>
        <div className="ml-4 flex items-center">
          <div className="text-sm text-gray-600 max-w-md leading-tight">
            <div>{t("header.description.line1")}</div>
            <div>{t("header.description.line2")}</div>
          </div>
        </div>
      </div>
      <div className="hidden md:inline-flex items-center justify-center">
        {auth?.user && (
          <div className="flex items-center gap-6">
            <NotificationIcon />
            <UserInfo />
            <LogoutTypePicker variant="header" />
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationIcon() {
  const { data: unreadCountResponse } = useQuery({
    queryKey: ["unreadNotificationsCount"],
    queryFn: () => getUnreadNotificationsCount(),
    refetchInterval: 60000,
  });

  const unreadCount = unreadCountResponse?.data || 0;
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate("/messages")}
      className="bg-white relative p-2 text-gray-400 hover:text-gray-600 cursor-pointer flex items-center"
    >
      <EmailIcon className="h-7 w-7 text-layout-palette" />
      {unreadCount > 0 && (
        <span className="absolute top-1 right-1 inline-flex items-center justify-center min-w-5 h-5 px-1 text-[10px] font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full border-2 border-white">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
}

function LithuanianStripe() {
  return (
    <div className="h-1.5 flex">
      <div className="w-1/3 bg-[#FCBA25]"></div>
      <div className="w-1/3 bg-[#136B39]"></div>
      <div className="w-1/3 bg-[#E22026]"></div>
    </div>
  );
}

function Header() {
  return (
    <div className="w-full">
      <LithuanianStripe />
      <header className="bg-white">
        <Navbar />
        <div className="max-w-screen-2xl mx-auto flex items-center justify-between px-8 py-6">
          <Logo />
        </div>
      </header>
    </div>
  );
}

interface UserInfoProps {
  className?: string;
}

function UserInfo({ className = "" }: UserInfoProps) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { user, representedEntity } = auth;

  if (!user) return null;

  return (
    <Menu as="div" className={`relative w-fit ${className}`}>
      {({ open }) => (
        <>
          <MenuButton
            className={`bg-white flex items-center gap-4 border border-gray-200 px-3 py-1.5 cursor-pointer hover:bg-gray-50 transition-colors ${
              open
                ? "rounded-t-md border-b-transparent bg-gray-50"
                : "rounded-md"
            }`}
          >
            <div className="flex flex-col items-end">
              <div className="flex items-center text-sm whitespace-nowrap">
                <span className="font-bold text-gray-900">
                  {user.firstName} {user.lastName}
                </span>
                <span className="text-gray-500 ml-1">({user.rin})</span>
              </div>
              {representedEntity && (
                <span className="flex whitespace-nowrap justify-end text-sm text-gray-900 mt-1">
                  <span>{t("header.userInfo.servicesRecipient")}:</span>
                  <span className="pl-1 font-medium">
                    {getPersonDisplayName(representedEntity)}
                  </span>
                </span>
              )}
            </div>
            <ChevronIcon
              direction="down"
              size={16}
              className={`text-gray-400 transition-transform duration-200 ${
                open ? "rotate-180" : ""
              }`}
            />
          </MenuButton>

          <MenuItems
            transition
            className="absolute left-0 w-full bg-white rounded-b-md shadow-lg border border-gray-200 border-t-0 z-50 focus:outline-none origin-top transition duration-200 ease-out data-closed:-translate-y-1 data-closed:opacity-0"
          >
            <div className="py-1">
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => navigate("/profile")}
                    className={`${
                      focus ? "bg-gray-100" : ""
                    } flex w-full items-center px-4 py-2 text-sm text-gray-700 cursor-pointer`}
                  >
                    {t("header.userInfo.businessEntityProfile")}
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    onClick={() => auth.showRepresentedEntityModal()}
                    className={`${
                      focus ? "bg-gray-100" : ""
                    } flex w-full items-center px-4 py-2 text-sm text-gray-700 cursor-pointer`}
                  >
                    {t("navigation.changeServicesRecipient")}
                  </button>
                )}
              </MenuItem>
            </div>
          </MenuItems>
        </>
      )}
    </Menu>
  );
}

export default Header;
